import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Alert } from "Components/_Shared/Alert";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as InfoIcon } from "Components/_Shared/Icons/Assets/info.svg";
import { Loader } from "Components/_Shared/Loader";
import type { PortfolioEvaluationItemEvaluationType } from "Infrastructure/ApiZfp/Schema/portfolioEvaluationItemEvaluationType";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { InvestmentsNsureRiskProfile } from "./InvestmentsNsureRiskProfile";
import { NsureSelectionContext } from "./InvestmentsNsureSelectionContext";
import { getRiskToleranceWarningItem } from "./utils";

export const InvestmentsNsureEvaluation = () => {
  const { t } = useResource();
  const { selection, evaluation } = useContext(NsureSelectionContext);
  const { evaluationPlan, items, okCount, hasWarning, isPending, isError, isDataStale, isOk } = evaluation;

  if (evaluation.isQuestionnaireLoading) return <Loader />;

  if (evaluation.isQuestionnaireNotFound) {
    return <Alert variant="warning" text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.EvaluationNotFound)} />;
  }

  if (!selection) return <Alert variant="gray" text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.SelectPortfolio)} />;

  if (isPending || isDataStale) return <Loader />;

  if (isError) return <Alert variant="error" text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.EvaluateError)} />;

  if (!evaluationPlan) return null;

  const riskToleranceItem = getRiskToleranceWarningItem(evaluationPlan);
  const evaluationLabels: Record<string, string> = {
    InvestorType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.InvestorType),
    CustomerCategory: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.CustomerCategory),
    RiskToleranceType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.RiskToleranceType),
    LossCapabilityType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.LossCapabilityType),
    InvestmentPreferenceType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.InvestmentPreferenceType),
    ESGType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.EsgType),
    InvestmentHorizon: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.InvestmentHorizon),
  };

  const renderItem = (type: PortfolioEvaluationItemEvaluationType) => (
    <Flex key={type} justifyContent="flex-start" alignItems="center" gap={Spacing.L}>
      <Flex alignItems="center" justifyContent="center" width={22} height={22} flexShrink={0} borderRadius={Radius.MAX}>
        {isOk(type) ? <CheckIcon sx={{ color: "success.dark", fontSize: 20 }} /> : <CloseIcon sx={{ color: "error.dark", fontSize: 20 }} />}
      </Flex>
      <Typography variant="body2">{evaluationLabels[type] ?? type}</Typography>
    </Flex>
  );

  return (
    <Stack spacing={Spacing.XXL}>
      <Stack spacing={Spacing.XXL} divider={<Divider />}>
        {riskToleranceItem && <InvestmentsNsureRiskProfile riskItem={riskToleranceItem} />}
        {items.length > 0 && (
          <Stack spacing={Spacing.M}>
            <Typography variant="body2" fontWeight={600}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.Title)}
            </Typography>
            <Stack spacing={Spacing.M} pt={Spacing.XS}>
              {items.map(renderItem)}
            </Stack>
          </Stack>
        )}
      </Stack>
      {hasWarning ? (
        <Alert
          variant="warning"
          bordered
          text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.WarningTitle)}
          action={
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" color="grey.300" display="block">
                    {okCount} {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.TargetMarketsMet)}
                  </Typography>
                  <Typography variant="caption" color="grey.50">
                    {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.WarningAlert)}
                  </Typography>
                </Box>
              }
            >
              <IconButton
                aria-label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.WarningTitle)}
                size="small"
                sx={{ color: "warning.dark", "&:hover": { color: "grey.800" }, ml: Spacing.S, p: 0 }}
              >
                <InfoIcon width="16px" />
              </IconButton>
            </Tooltip>
          }
        />
      ) : (
        <Alert variant="success" bordered text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.SuccessTitle)} />
      )}
    </Stack>
  );
};
